import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Radio, Mic2, Users, User, LogOut, Settings, BarChart3, Music, Clock,
  TrendingUp, Wifi, WifiOff, ChevronRight, Crown, LifeBuoy, CreditCard, Gift, Disc3, Globe,
  Mail, Mic, Layout, Calendar, Code2, Activity, Target, BookOpen, PhoneCall, Bot
} from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import { OverviewTab } from "../components/dashboard/OverviewTab";
import { StationTab } from "../components/dashboard/StationTab";
import { AnalyticsTab } from "../components/dashboard/AnalyticsTab";
import { TeamTab } from "../components/dashboard/TeamTab";
import { LibraryTab } from "../components/dashboard/LibraryTab";
import { BillingTab } from "../components/dashboard/BillingTab";
import { SupportTab } from "../components/dashboard/SupportTab";
import { SettingsTab } from "../components/dashboard/SettingsTab";
import { WhiteLabelTab } from "../components/dashboard/WhiteLabelTab";
import { LiquidsoapTab } from "../components/dashboard/LiquidsoapTab";
import { PodcastTab } from "../components/dashboard/PodcastTab";
import { MyTracksTab } from "../components/dashboard/MyTracksTab";
import { EmbedTab } from "../components/dashboard/EmbedTab";
import { StandardWidgetTab } from "../components/dashboard/StandardWidgetTab";
import { PageBuilderTab } from "../components/dashboard/PageBuilderTab";
import { ListenerOverview } from "../components/dashboard/ListenerOverview";
import SEO from "../components/SEO";
import DashboardClock from "../components/dashboard/DashboardClock";
import AffiliateDashboardSection from "../components/dashboard/AffiliateDashboard";
import GoLiveModal from "../components/dashboard/GoLiveModal";
import UploadMusicModal from "../components/dashboard/UploadMusicModal";
import { WelcomeModal, OnboardingChecklist } from "../components/dashboard/Onboarding";
import { EmailTab } from "../components/dashboard/EmailTab";
import { ArtistProfileTab } from "../components/dashboard/ArtistProfileTab";
import { BroadcastingTab } from "../components/dashboard/BroadcastingTab";
import { ShowScheduleTab } from "../components/dashboard/ShowScheduleTab";
import { DJProfilesTab } from "../components/dashboard/DJProfilesTab";
import { DomainsTab } from "../components/dashboard/DomainsTab";
import { StationHealthTab } from "../components/dashboard/StationHealthTab";
import IncomingSubmissionsTab from "../components/dashboard/IncomingSubmissionsTab";
import HowToGuideTab from "../components/dashboard/HowToGuideTab";
import { CallInTab } from "../components/dashboard/CallInTab";

const THEME_STYLES = {
  classic: { bg: '#0f172a', sidebar: '#020617', accent: '#3b82f6', accentHover: '#2563eb', accentBg: 'rgba(59,130,246,0.15)', border: 'rgba(148,163,184,0.12)', headerBg: 'rgba(2,6,23,0.7)' },
  modern: { bg: '#030712', sidebar: '#000000', accent: '#a855f7', accentHover: '#9333ea', accentBg: 'rgba(168,85,247,0.15)', border: 'rgba(255,255,255,0.1)', headerBg: 'rgba(0,0,0,0.5)' },
  dark: { bg: '#0a0a0a', sidebar: '#000000', accent: '#10b981', accentHover: '#059669', accentBg: 'rgba(16,185,129,0.12)', border: 'rgba(255,255,255,0.06)', headerBg: 'rgba(0,0,0,0.8)' },
  minimal: { bg: '#18181b', sidebar: '#09090b', accent: '#a1a1aa', accentHover: '#d4d4d8', accentBg: 'rgba(161,161,170,0.1)', border: 'rgba(255,255,255,0.08)', headerBg: 'rgba(24,24,27,0.8)' },
  premium: { bg: '#1c1917', sidebar: '#0c0a09', accent: '#f59e0b', accentHover: '#d97706', accentBg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.1)', headerBg: 'rgba(12,10,9,0.8)' },
};

const TAB_TITLES = {
  overview: 'Dashboard', station: 'My Channel', analytics: 'Analytics', team: 'Team Management',
  library: 'Music Library', mytracks: 'My Tracks', affiliate: 'Affiliate Program', billing: 'Billing & Payments',
  support: 'Support', domains: 'Domain Settings', settings: 'Settings', whitelabel: 'White Label Player',
  pagebuilder: 'Station Page Builder', liquidsoap: 'Liquidsoap Editor', podcast: 'Podcast & Recorded Shows',
  embed: 'Embed Hub', widgets: 'Standard Widget', email: 'Email Campaigns', artistprofile: 'Artist Profile', broadcasting: 'Broadcasting',
  djprofiles: 'DJ Profiles', shows: 'Show Schedule', health: 'Station Health', incoming: 'Incoming Submissions', guide: 'How-To Guide', callin: 'Call-In Studio',
};

const Dashboard = () => {
  const { user, logout, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = user;
  const authToken = token;

  // Navigation
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dashboardTheme, setDashboardTheme] = useState(() => localStorage.getItem('dashboard_theme') || 'modern');
  const theme = THEME_STYLES[dashboardTheme] || THEME_STYLES.modern;

  // Stats (shared across tabs)
  const [stats, setStats] = useState(null);
  const [listenerHistory, setListenerHistory] = useState([]);

  // Modal toggles
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [showUploadMusicModal, setShowUploadMusicModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const handleThemeChange = (t) => { setDashboardTheme(t); localStorage.setItem('dashboard_theme', t); };

  const refreshStats = useCallback(async () => {
    if (!currentUser || !authToken) return;
    try {
      const headers = { 'Content-Type': 'application/json' };
      const t = localStorage.getItem("token") || authToken;
      if (t) headers['Authorization'] = `Bearer ${t}`;
      const [statsRes, historyRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/stats`, { credentials: 'omit', headers }),
        fetch(`${BACKEND_URL}/api/dashboard/listener-history`, { credentials: 'omit', headers }),
      ]);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData?.station_name && currentUser) currentUser.station_name = statsData.station_name;
        setStats(statsData);
      }
      if (historyRes.ok) setListenerHistory(await historyRes.json());
    } catch {}
  }, [currentUser, authToken]);

  useEffect(() => {
    if (!currentUser && !authLoading && !authToken) { navigate('/'); return; }
    if (!currentUser || !authToken) return;

    // Check for first-time user welcome
    const welcomeKey = `welcomed_${currentUser.email}`;
    if (!localStorage.getItem(welcomeKey) && currentUser.plan !== 'listener') {
      setShowWelcome(true);
      localStorage.setItem(welcomeKey, '1');
    }

    refreshStats();
    const interval = setInterval(refreshStats, 30000);
    return () => clearInterval(interval);
  }, [currentUser, authToken, authLoading, navigate, refreshStats]);

  // Handle tab from route state
  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state?.tab]);

  // Admin chat heartbeat — keeps admin marked "online" while dashboard is open
  useEffect(() => {
    if (!currentUser || !authToken) return;
    const isAdmin = currentUser?.is_admin || currentUser?.email === 'wnuv98.5@gmail.com' || currentUser?.plan === 'admin';
    if (!isAdmin) return;

    const heartbeat = () => {
      fetch(`${BACKEND_URL}/api/chat/admin/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => {});
    };
    heartbeat(); // immediately on login
    const hbInterval = setInterval(heartbeat, 30000); // every 30s

    // Go offline on tab close / navigate away
    const goOffline = () => {
      navigator.sendBeacon?.(`${BACKEND_URL}/api/chat/admin/offline`);
    };
    window.addEventListener('beforeunload', goOffline);
    return () => {
      clearInterval(hbInterval);
      window.removeEventListener('beforeunload', goOffline);
      goOffline();
    };
  }, [currentUser, authToken]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (!currentUser && !authLoading && !authToken) { navigate('/'); return null; }
  if (!currentUser) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Radio className="w-12 h-12 text-purple-400 animate-pulse" /></div>;
  }

  const isListener = currentUser?.plan === 'listener' || currentUser?.user_type === 'listener';
  const isAdmin = currentUser?.is_admin || currentUser?.email === 'wnuv98.5@gmail.com' || currentUser?.plan === 'admin';
  const hasPro = ['professional', 'premium', 'admin'].includes(currentUser?.plan);
  const planColors = { starter: 'text-blue-400', professional: 'text-purple-400', premium: 'text-yellow-400', listener: 'text-green-400', byos: 'text-cyan-400', admin: 'text-red-400' };

  const sections = [
    { label: null, items: [{ id: 'overview', icon: BarChart3, label: 'Overview', listener: true }] },
    { label: 'CONTENT', items: [
      { id: 'station', icon: Radio, label: 'My Channel' }, { id: 'mytracks', icon: Disc3, label: 'My Tracks' },
      { id: 'incoming', icon: Target, label: 'Submissions' },
      { id: 'library', icon: Music, label: 'Music Library' }, { id: 'podcast', icon: Mic2, label: 'Podcast' },
      { id: 'callin', icon: PhoneCall, label: 'Call-In Studio' },
      { id: 'team', icon: Users, label: 'Team' },
    ]},
    { label: 'GROWTH', items: [{ id: 'analytics', icon: TrendingUp, label: 'Analytics' }, { id: 'affiliate', icon: Gift, label: 'Affiliate', listener: true }] },
    { label: 'TOOLS', items: [{ id: 'embed', icon: Globe, label: 'Embed Hub' }, { id: 'widgets', icon: Radio, label: 'Standard Widget' }, { id: 'email', icon: Mail, label: 'Email' }] },
    { label: 'PROFILE', items: [{ id: 'artistprofile', icon: Mic, label: 'Artist Profile', listener: true }] },
    { label: 'SYSTEM', items: [
      { id: 'health', icon: Activity, label: 'Station Health' },
      { id: 'domains', icon: Globe, label: 'Domains' }, { id: 'settings', icon: Settings, label: 'Settings', listener: true },
      { id: 'billing', icon: CreditCard, label: 'Billing' }, { id: 'support', icon: LifeBuoy, label: 'Support', listener: true },
      { id: 'guide', icon: BookOpen, label: 'How-To Guide', listener: true },
    ]},
    { label: 'ADVANCED', items: [
      { id: 'broadcasting', icon: Radio, label: 'Broadcasting' },
      ...(hasPro ? [{ id: 'liquidsoap', icon: Code2, label: 'Liquidsoap' }] : []),
      { id: 'djprofiles', icon: Mic2, label: 'DJ Profiles' }, { id: 'shows', icon: Calendar, label: 'Shows' },
      { id: 'whitelabel', icon: Crown, label: 'White Label' }, { id: 'pagebuilder', icon: Layout, label: 'Page Builder' },
    ]},
  ];

  return (
    <div className="min-h-screen flex" style={{ background: theme.bg }}>
      <SEO title="Dashboard" noindex={true} />

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 flex flex-col`} style={{ background: theme.sidebar, borderRight: `1px solid ${theme.border}` }}>
        <div className="p-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-3">
            <img src="/logo-header.png" alt="DJsandMCMedia™" className={sidebarOpen ? "h-8 w-auto" : "h-8 w-8 object-contain object-left"} />
          </div>
          {sidebarOpen && <DashboardClock stationName={currentUser?.station_name} />}
        </div>
        <nav className="flex-1 p-3 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#27272a transparent' }}>
          {sections.map((section, si) => {
            const visibleItems = isListener ? section.items.filter(t => t.listener) : section.items;
            if (visibleItems.length === 0) return null;
            return (
              <div key={si} className={si > 0 ? 'mt-3' : ''}>
                {section.label && sidebarOpen && <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/25">{section.label}</p>}
                {!sidebarOpen && si > 0 && section.label && <div className="mx-3 my-2 border-t border-white/5" />}
                <div className="space-y-0.5">
                  {visibleItems.map(item => (
                    <button key={item.id} onClick={() => setActiveTab(item.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition text-sm"
                      style={activeTab === item.id ? { background: theme.accentBg, color: theme.accent } : { color: 'rgba(255,255,255,0.6)' }}
                      onMouseEnter={e => { if (activeTab !== item.id) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#fff'; }}}
                      onMouseLeave={e => { if (activeTab !== item.id) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}}
                      data-testid={`nav-${item.id}`}>
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {sidebarOpen && <span>{item.label}</span>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {isListener && (
            <button onClick={() => navigate('/discover')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition text-green-400 hover:bg-green-500/10 text-sm mt-2" data-testid="nav-discover">
              <Radio className="w-5 h-5 flex-shrink-0" />{sidebarOpen && <span>Discover Stations</span>}
            </button>
          )}
          {isListener && sidebarOpen && (
            <div className="mt-4 mx-1 rounded-xl p-3 text-center" style={{ background: theme.accentBg, border: `1px solid ${theme.border}` }}>
              <p className="text-white/50 text-xs mb-2">Want your own station?</p>
              <button onClick={() => navigate('/#pricing')} className="w-full text-white text-xs font-medium py-2 rounded-lg transition" style={{ background: theme.accent }} data-testid="listener-upgrade-btn">Start Broadcasting</button>
            </div>
          )}
          {isAdmin && (
            <>
              {sidebarOpen && <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-yellow-500/50">Admin</p>}
              {!sidebarOpen && <div className="mx-3 my-2 border-t border-yellow-500/20" />}
              {/* AI Studio */}
              <button
                onClick={() => navigate('/ai-studio')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition text-cyan-400 hover:bg-cyan-500/10 text-sm"
                data-testid="sidebar-ai-studio"
              >
                <Bot className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>AI Studio</span>}
              </button>
              <button onClick={() => navigate('/admin')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition text-yellow-400 hover:bg-yellow-500/10 text-sm" data-testid="nav-admin">
                <Users className="w-5 h-5 flex-shrink-0" />{sidebarOpen && <span>Admin Panel</span>}
              </button>
            </>
          )}
        </nav>
        <div className="p-4" style={{ borderTop: `1px solid ${theme.border}` }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="w-full flex items-center justify-center p-2 text-white/50 hover:text-white transition">
            <ChevronRight className={`w-5 h-5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10" style={{ background: theme.headerBg, borderBottom: `1px solid ${theme.border}` }}>
          <div>
            <h1 className="text-xl font-bold text-white">{TAB_TITLES[activeTab] || 'Dashboard'}</h1>
            <p className="text-white/50 text-sm">{isListener ? 'Listener Dashboard' : stats?.station_name || currentUser.station_name || 'Your Station'}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-xl">
              {isListener ? (<><Music className="w-4 h-4 text-green-400" /><span className="text-green-400 text-sm font-medium">Listener</span></>)
                : stats?.is_live ? (<><Wifi className="w-4 h-4 text-red-400 animate-pulse" /><span className="text-red-400 text-sm font-medium">LIVE DJ</span></>)
                : stats?.now_playing || stats?.listeners > 0 ? (<><Wifi className="w-4 h-4 text-green-400" /><span className="text-green-400 text-sm font-medium">On Air</span></>)
                : currentUser.azuracast_station_id ? (<><Wifi className="w-4 h-4 text-purple-400" /><span className="text-purple-400 text-sm font-medium">Connected</span></>)
                : (<><WifiOff className="w-4 h-4 text-white/40" /><span className="text-white/40 text-sm">No Station</span></>)}
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ background: theme.accent }}>
                {currentUser.picture ? <img src={currentUser.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-white" />}
              </div>
              <div className="hidden md:block">
                <p className="text-white text-sm font-medium">{currentUser.name}</p>
                <p className={`text-xs capitalize ${planColors[currentUser.trial_active ? currentUser.trial_plan : currentUser.plan] || 'text-white/50'}`}>
                  {currentUser.trial_active ? `Trial: ${['premium'].includes(currentUser.trial_plan) ? 'Premium Channel' : currentUser.trial_plan === 'professional' ? 'Pro Channel' : 'Starter Channel'}`
                    : currentUser.plan === 'listener' ? 'Listener'
                    : `${['premium'].includes(currentUser.plan) ? 'Premium Channel' : currentUser.plan === 'professional' ? 'Pro Channel' : currentUser.plan === 'byos' ? 'BYOS Channel' : 'Starter Channel'}`}
                </p>
              </div>
              <button onClick={handleLogout} className="p-2 text-white/50 hover:text-red-400 transition" data-testid="dashboard-logout"><LogOut className="w-5 h-5" /></button>
            </div>
          </div>
        </header>

        {/* Email Verification Banner */}
        {currentUser && !currentUser.email_verified && !currentUser.is_admin && (
          <div className="mx-6 mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="verify-email-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-amber-400" /></div>
              <div><p className="text-white font-medium text-sm">Verify your email address</p><p className="text-white/50 text-xs">Check your inbox for a verification link. Some features are limited until verified.</p></div>
            </div>
            <button onClick={async () => {
              try { const res = await fetch(`${BACKEND_URL}/api/auth/resend-verification`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }); if (res.ok) alert("Verification email sent!"); else alert((await res.json()).detail || "Failed to resend."); } catch { alert("Failed to send. Try again later."); }
            }} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-semibold px-4 py-2 rounded-lg transition whitespace-nowrap" data-testid="resend-verification-btn">Resend Email</button>
          </div>
        )}

        {/* Trial Banner */}
        {currentUser.trial_active && currentUser.trial_end && (
          <div className="mx-6 mt-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="trial-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-purple-400" /></div>
              <div>
                <p className="text-white font-medium text-sm">Free Trial — <span className="capitalize text-purple-300">{currentUser.trial_plan === 'premium' ? 'Featured Channel' : currentUser.trial_plan === 'professional' ? 'Pro Channel' : 'Starter Channel'}</span></p>
                <p className="text-white/50 text-xs">{(() => { const end = new Date(currentUser.trial_end); const diff = end - new Date(); if (diff <= 0) return 'Trial expired'; const d = Math.floor(diff / 864e5); const h = Math.floor((diff % 864e5) / 36e5); return `${d}d ${h}h remaining — ends ${end.toLocaleDateString()}`; })()}</p>
              </div>
            </div>
            <button onClick={() => { window.location.href = '/#pricing'; }} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg font-medium transition flex-shrink-0" data-testid="trial-upgrade-btn">Upgrade Now</button>
          </div>
        )}

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <>
              <OnboardingChecklist currentUser={currentUser} authToken={authToken} onTabChange={setActiveTab} />
              {isListener ? (
                <ListenerOverview currentUser={currentUser} authToken={authToken} navigate={navigate} />
              ) : (
                <OverviewTab stats={stats} listenerHistory={listenerHistory} currentUser={currentUser} navigate={navigate}
                  handleGoLive={() => setShowGoLiveModal(true)} setShowUploadMusicModal={setShowUploadMusicModal}
                  showUpgradeModal={showUpgradeModal} setShowUpgradeModal={setShowUpgradeModal} refreshStats={refreshStats} />
              )}
            </>
          )}
          {activeTab === 'station' && <StationTab currentUser={currentUser} stats={stats} authToken={authToken} />}
          {activeTab === 'analytics' && <AnalyticsTab currentUser={currentUser} />}
          {activeTab === 'team' && <TeamTab currentUser={currentUser} />}
          {activeTab === 'library' && <LibraryTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'billing' && <BillingTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'support' && <SupportTab authToken={authToken} />}
          {activeTab === 'domains' && <DomainsTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'settings' && <SettingsTab currentUser={currentUser} authToken={authToken} dashboardTheme={dashboardTheme} onThemeChange={handleThemeChange} />}
          {activeTab === 'whitelabel' && <WhiteLabelTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'pagebuilder' && <PageBuilderTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'liquidsoap' && <LiquidsoapTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'podcast' && <PodcastTab token={authToken} currentUser={currentUser} />}
          {activeTab === 'mytracks' && <MyTracksTab currentUser={currentUser} />}
          {activeTab === 'affiliate' && <div className="space-y-6" data-testid="affiliate-dashboard-tab"><AffiliateDashboardSection user={currentUser} /></div>}
          {activeTab === 'embed' && <EmbedTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'widgets' && <StandardWidgetTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'email' && <EmailTab currentUser={currentUser} authToken={authToken} isAdmin={currentUser?.is_admin} />}
          {activeTab === 'artistprofile' && <ArtistProfileTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'broadcasting' && <BroadcastingTab currentUser={currentUser} />}
          {activeTab === 'djprofiles' && <DJProfilesTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'shows' && <ShowScheduleTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'health' && <StationHealthTab currentUser={currentUser} authToken={authToken} onTabChange={setActiveTab} />}
          {activeTab === 'incoming' && <IncomingSubmissionsTab authToken={authToken} />}
          {activeTab === 'callin' && <CallInTab currentUser={currentUser} authToken={authToken} />}
          {activeTab === 'guide' && <HowToGuideTab currentUser={currentUser} onTabChange={setActiveTab} />}
        </div>
      </main>

      <GoLiveModal show={showGoLiveModal} onClose={() => setShowGoLiveModal(false)} currentUser={currentUser} authToken={authToken} />
      <UploadMusicModal show={showUploadMusicModal} onClose={() => setShowUploadMusicModal(false)} authToken={authToken} />
      {showWelcome && currentUser && <WelcomeModal currentUser={currentUser} onClose={() => setShowWelcome(false)} />}
    </div>
  );
};

export default Dashboard;
